import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Search, Menu, X, Phone, Mail, ArrowRight, Briefcase } from "lucide-react";
import { Button } from "./ui/button";
import { LOGO_URL } from "../mock";
import { PRODUCTS_MENU, SOLUTIONS_MENU, SKILLS_PROGRAMS_MENU } from "../data/topNavMenus";
import { PARTNER_LOGOS } from "../data/partnerLogos";
import { PARTNER_OFFERINGS } from "../mock";

const partnerSlug = (name) => name.toLowerCase().replace(/[()]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const PARTNER_NAME_ALIASES = { "Google Cloud Platform (GCP)": "Google Cloud" };
const partnerLinkFor = (label) => {
  const canonical = PARTNER_NAME_ALIASES[label] || label;
  const match = PARTNER_OFFERINGS.find((p) => p.name === canonical);
  return match ? `/offerings/partner/${partnerSlug(match.name)}` : "/offerings/partner";
};

/* ============ COLUMN MEGA MENU (Products / Solutions) ============ */
const ColumnsMegaMenu = ({ columns, onClose, width = 560 }) => (
  <div
    data-testid="mega-menu"
    className="absolute top-full left-0 mt-0 bg-[#0E1F4A] text-gray-100 shadow-2xl border border-white/10 rounded-b-xl overflow-hidden"
    style={{ width: `${width}px`, maxWidth: "96vw" }}
  >
    <div className={`grid ${columns.length > 1 ? "grid-cols-2" : "grid-cols-1"} gap-2 p-5`}>
      {columns.map((col) => (
        <div key={col.section} className="space-y-1">
          <div className="text-[11px] font-bold tracking-widest text-[#6BBE32] mb-2">{col.section}</div>
          {col.items.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.label}
                to={it.to}
                onClick={onClose}
                data-testid={`menu-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-white/10 hover:text-white transition-colors"
              >
                {Icon ? <Icon size={15} className="text-[#8BD835] shrink-0" /> : null}
                <span>{it.label}</span>
              </Link>
            );
          })}
        </div>
      ))}
    </div>
  </div>
);

/* ============ SINGLE COLUMN MEGA MENU (Skills Programs) ============ */
const SingleColMegaMenu = ({ items, onClose, eyebrow }) => (
  <div
    data-testid="mega-menu-skills"
    className="absolute top-full left-0 mt-0 w-[340px] max-w-[96vw] bg-[#0E1F4A] text-gray-100 shadow-2xl border border-white/10 rounded-b-xl overflow-hidden"
  >
    <div className="p-5 space-y-1">
      {eyebrow && <div className="text-[11px] font-bold tracking-widest text-[#6BBE32] mb-2">{eyebrow}</div>}
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <Link
            key={it.label}
            to={it.to}
            onClick={onClose}
            data-testid={`menu-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-white/10 hover:text-white transition-colors"
          >
            {Icon ? <Icon size={15} className="text-[#8BD835] shrink-0" /> : null}
            <span>{it.label}</span>
          </Link>
        );
      })}
    </div>
  </div>
);

/* ============ PARTNER OFFERINGS MENU ============ */
const PARTNER_LIST = [
  "Amazon Web Services (AWS)", "Microsoft", "Google Cloud Platform (GCP)", "SAP", "Red Hat", "CompTIA",
  "PeopleCert", "ISTQB", "Scrum Alliance", "ASCM – Supply Chain Management", "Siemens", "Blockchain Training Alliance",
  "Android ATC", "PECB", "Oracle", "IBM", "Automation Anywhere", "Dell EMC", "MuleSoft", "Chef", "GitHub", "Elastic",
  "Lightbend", "Mirantis", "ISC2", "Check Point", "Cloud Security Alliance (CSA)", "Snowflake", "Broadcom"
];

const PartnersMegaMenu = ({ onClose }) => (
  <div className="absolute top-full right-0 mt-0 w-[600px] max-w-[96vw] bg-[#0E1F4A] text-gray-100 shadow-2xl border border-white/10 rounded-b-xl overflow-hidden">
    <div className="px-5 pt-5 pb-3 border-b border-white/10">
      <div className="text-[11px] font-bold tracking-widest text-[#6BBE32]">PARTNER OFFERINGS</div>
      <div className="text-sm text-gray-300 mt-1">Authorised training programs from leading technology vendors</div>
    </div>
    <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 p-3 max-h-[440px] overflow-y-auto">
      {PARTNER_LIST.map((p) => {
        const logo = PARTNER_LOGOS[p];
        return (
          <Link
            key={p}
            to={partnerLinkFor(p)}
            onClick={onClose}
            data-testid={`partner-item-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-white/10 text-sm text-gray-200 hover:text-white transition-colors"
          >
            {logo ? (
              <img
                src={logo}
                alt={`${p} logo`}
                className="w-5 h-5 object-contain shrink-0"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = "inline-block"); }}
              />
            ) : null}
            <Briefcase size={13} className="text-[#F57C20] shrink-0" style={{ display: logo ? "none" : "inline-block" }} />
            <span className="truncate">{p}</span>
          </Link>
        );
      })}
    </div>
    <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between bg-[#0A1F5C]">
      <span className="text-xs text-gray-300">29 authorised vendor partners</span>
      <Link
        to="/offerings/partner"
        onClick={onClose}
        className="inline-flex items-center gap-1 text-xs font-bold tracking-wider text-[#F57C20] hover:gap-2 transition-all"
      >
        EXPLORE ALL PARTNERS <ArrowRight size={13} />
      </Link>
    </div>
  </div>
);

/* ============ HEADER ============ */
const Header = () => {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const navigate = useNavigate();
  const closeAll = () => setOpenMenu(null);

  const onSearch = (e) => {
    e.preventDefault();
    if (!searchQ.trim()) return;
    navigate(`/offerings/all?q=${encodeURIComponent(searchQ.trim())}`);
    setSearchOpen(false);
    setSearchQ("");
  };

  const navItem = (key, label, menuNode) => (
    <div className="relative" onMouseEnter={() => setOpenMenu(key)}>
      <button
        data-testid={`nav-${key}`}
        className="flex items-center gap-1 px-4 py-2 text-[#15348C] font-medium hover:text-[#6BBE32] transition-colors"
      >
        {label} <ChevronDown size={16} className={`transition-transform ${openMenu === key ? "rotate-180" : ""}`} />
      </button>
      {openMenu === key && menuNode}
    </div>
  );

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-100">
      <div className="bg-[#15348C] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex justify-end items-center gap-5">
          <a href="tel:+918045678900" className="hidden sm:flex items-center gap-1 hover:text-[#8BD835] transition-colors"><Phone size={12} /> +91 80 4567 8900</a>
          <a href="mailto:info@skillsgain.com" className="flex items-center gap-1 hover:text-[#8BD835] transition-colors"><Mail size={12} /> info@skillsgain.com</a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-24 lg:h-28" onMouseLeave={closeAll}>
        <Link to="/" className="flex items-center gap-2 mr-6 shrink-0" data-testid="header-logo">
          <img src={LOGO_URL} alt="SkillsGain" className="h-20 lg:h-24 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItem("products", "Products", <ColumnsMegaMenu columns={PRODUCTS_MENU} onClose={closeAll} width={420} />)}
          {navItem("solutions", "Solutions", <ColumnsMegaMenu columns={SOLUTIONS_MENU} onClose={closeAll} width={560} />)}
          {navItem("skills", "Skills Programs", <SingleColMegaMenu items={SKILLS_PROGRAMS_MENU} onClose={closeAll} eyebrow="SKILLS PROGRAMS" />)}
          {navItem("partner", "Partner Offerings", <PartnersMegaMenu onClose={closeAll} />)}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative">
            <button onClick={() => setSearchOpen(o => !o)} aria-label="Search"
              data-testid="search-toggle"
              className="w-10 h-10 flex items-center justify-center rounded-full text-[#15348C] hover:bg-[#15348C]/10 transition-colors">
              {searchOpen ? <X size={18} /> : <Search size={18} />}
            </button>
            {searchOpen && (
              <form onSubmit={onSearch} className="absolute right-0 top-full mt-2 w-[min(90vw,360px)] bg-white border border-gray-200 shadow-2xl rounded-xl p-2 flex items-center gap-2">
                <Search size={16} className="ml-2 text-gray-400 shrink-0" />
                <input autoFocus value={searchQ} onChange={e => setSearchQ(e.target.value)}
                  placeholder="Search courses, certifications..."
                  className="flex-1 bg-transparent outline-none text-sm text-[#15348C] placeholder-gray-400" />
                <button type="submit" className="bg-[#15348C] hover:bg-[#0A1F5C] text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors">Go</button>
              </form>
            )}
          </div>

          <Link to="/contact" className="hidden md:inline-block" data-testid="header-expert-cta">
            <Button className="bg-[#15348C] hover:bg-orange-500 text-white font-semibold px-5 rounded-md transition-colors">Talk to Our Expert</Button>
          </Link>
          <button className="lg:hidden p-2 text-[#15348C]" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 max-h-[75vh] overflow-y-auto" data-testid="mobile-nav">
          <div className="px-4 py-3 space-y-1">
            <MobileGroup label="Products" sections={PRODUCTS_MENU} onClose={() => setMobileOpen(false)} />
            <MobileGroup label="Solutions" sections={SOLUTIONS_MENU} onClose={() => setMobileOpen(false)} />
            <MobileFlatGroup label="Skills Programs" items={SKILLS_PROGRAMS_MENU} onClose={() => setMobileOpen(false)} />
            <MobileFlatGroup label="Partner Offerings" items={PARTNER_LIST.map(p => ({ label: p, to: partnerLinkFor(p) }))} onClose={() => setMobileOpen(false)} />
            <Link to="/contact" onClick={() => setMobileOpen(false)} data-testid="mobile-expert-cta"
              className="block py-2 mt-2 bg-[#15348C] text-white font-semibold rounded text-center">Talk to Our Expert</Link>
          </div>
        </div>
      )}
    </header>
  );
};

const MobileGroup = ({ label, sections, onClose }) => (
  <details className="py-2">
    <summary className="text-[#15348C] font-medium cursor-pointer">{label}</summary>
    <div className="pl-4 mt-2 space-y-2 text-sm">
      {sections.map((sec) => (
        <div key={sec.section}>
          <div className="text-[10px] font-bold tracking-widest text-[#6BBE32]">{sec.section}</div>
          {sec.items.map((it) => (
            <Link key={it.label} to={it.to} onClick={onClose} className="block py-1 text-gray-700">{it.label}</Link>
          ))}
        </div>
      ))}
    </div>
  </details>
);

const MobileFlatGroup = ({ label, items, onClose }) => (
  <details className="py-2">
    <summary className="text-[#15348C] font-medium cursor-pointer">{label}</summary>
    <div className="pl-4 mt-2 space-y-1 text-sm">
      {items.map((it) => (
        <Link key={it.label} to={it.to} onClick={onClose} className="block py-1 text-gray-700">{it.label}</Link>
      ))}
    </div>
  </details>
);

export default Header;
